import axios from "axios";
import axiosRetry from 'axios-retry';
const https = require('https');
const http = require('http');

const isSSR = (typeof window === 'undefined');
axiosRetry(axios, { retries: 3 });


let httpsAgent;
let httpAgent;
if (isSSR) {
  httpAgent = new http.Agent({
    keepAlive: true,
    keepAliveMsecs: 1000,
    maxSockets: 50,
  });
  httpsAgent = new https.Agent({
    keepAlive: true,
    keepAliveMsecs: 1000,
    maxSockets: 50,
  });
}

const axiosClient = axios.create({
  httpAgent,
  httpsAgent,
});
axiosRetry(axiosClient, {
  retries: 3,
  retryDelay: axiosRetry.exponentialDelay,
});

// Server side gaan de API calls direct naar de api service in het cluster (INTERNAL_API_URL, bv. http://api:3000)
// in plaats van via Cloudflare, dat requests met een CF-Connecting-IP header weigert (error code 1000).
// API_URL wordt in de build ingevuld, INTERNAL_API_URL runtime: met bracket notatie vervangt webpack hem niet
const internalApiUrl = isSSR ? process.env['INTERNAL_API_URL']?.replace(/\/$/, '') : undefined;

// Deze /api/v2 routes zijn van deze Next app zelf (pages/api/v2), niet van de api
const nextApiRoutes = /^\/(countries|states|appversion|healthcheck)(\/|\?|$)/;

function getInternalUrl(url): string | undefined {
  if(!internalApiUrl || typeof url !== 'string' || !url.startsWith(process.env.API_URL + '/'))
    return undefined;

  const path = url.substring(process.env.API_URL.length);
  if(nextApiRoutes.test(path))
    return undefined;

  return internalApiUrl + path;
}

type FetchReturnValue = [
  Promise<any>,
  () => void,
]

function FetchJSON(url, _config?:any): FetchReturnValue {
  const config = {
    autoRefreshToken: true,
    ..._config,
  }
  const controller = config.abortController ? config.abortController : new AbortController();
  const promise = new Promise((resolve, reject) => {

    const headers = {...config.headers};
    const internalUrl = getInternalUrl(url);

    // IP van de bezoeker van dit request doorgeven, uit de request context van server.js.
    // Niet via axiosClient.defaults: die zijn gedeeld tussen gelijktijdige requests
    // CF-Connecting-IP en X-Cloudflare-Secret ongewijzigd doorsturen, de backend valideert het secret.
    // Alleen naar de api in het cluster, via Cloudflare zou het request geweigerd worden
    if(isSSR) {
      const { forwardedFor, cfConnectingIp, cloudflareSecret } = global.requestContext?.getStore() ?? {};
      if(forwardedFor)
        headers['X-Forwarded-For'] = forwardedFor;
      if(internalUrl && cfConnectingIp && cloudflareSecret) {
        headers['CF-Connecting-IP'] = cfConnectingIp;
        headers['X-Cloudflare-Secret'] = cloudflareSecret;
      }
    }

    if(config.token)
      headers['Authorization'] = `Bearer ${config.token.token}`;
    
    const signal = config.signal ? config.signal : controller.signal;
    const req = axiosClient(internalUrl ?? url, {
      signal,
      method: config.method,
      data: config.data,
      withCredentials: config.withCredentials,
      headers,
    });

    req.then(response => {
      // @ts-ignore
      response.statusCode = response.status;
      resolve(response);
    })
    .catch(e => {
      if(e?.response?.status===401 && config.onTokenExpired)
      {
        config.onTokenExpired()
        .then((token) => {
          const [promise] = FetchJSON(url, {
            ...config,
            autoRefreshToken: false,
            token: token,
            signal,
          });
          promise.then(response => {
            resolve(response);
          })
          .catch(e => {
            reject(e);
          })

        })
        .catch(e => {
          reject(e);
        });
      }else
      {
        if(e.constructor.name === 'AbortError' || e.constructor.name === 'CanceledError')
        {
          // ignore aborts and cancels
          return;
        }

        reject(e);
      }
    });
    
  });

  return [promise, () => {
    controller.abort();
  }];
}

export {
  axiosClient,
  FetchJSON,
};